import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
} from '@mui/material';
import AnimatedElement from './AnimatedElement';

const HeroSection = () => {
  const theme = useTheme();
  const sliderRef = useRef(null);

  // Slider-Bilder aus dem hero-slider Ordner
  const sliderImages = [
    {
      id: 1,
      src: "/hero-slider/hero_bild_1.webp",
      alt: "Corps Guestphalia - Bild 1"
    },
    {
      id: 2,
      src: "/hero-slider/hero_bild_2.webp",
      alt: "Corps Guestphalia - Bild 2"
    },
    {
      id: 3,
      src: "/hero-slider/hero_bild_3.webp",
      alt: "Corps Guestphalia - Bild 3"
    },
    {
      id: 4,
      src: "/hero-slider/hero_bild_4.webp",
      alt: "Corps Guestphalia - Bild 4"
    },
    {
      id: 5,
      src: "/hero-slider/hero_bild_5.webp",
      alt: "Corps Guestphalia - Bild 5"
    },
    {
      id: 6,
      src: "/hero-slider/hero_bild_6.webp",
      alt: "Corps Guestphalia - Bild 6"
    }
  ];

  useEffect(() => {
    // Initialisiere Slick Slider nach Component Mount
    const initSlider = () => {
      if (window.$ && sliderRef.current && window.$.fn.slick) {
        try {
          // Prüfe ob Slider bereits initialisiert ist
          if (window.$(sliderRef.current).hasClass('slick-initialized')) {
            return;
          }

          // Prüfe ob Slides vorhanden sind
          const slides = sliderRef.current.children;
          if (slides.length === 0) {
            return;
          }

          window.$(sliderRef.current).slick({
            dots: false,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: true,
            autoplaySpeed: 5000,
            fade: false,
            cssEase: 'ease-in-out',
            arrows: true,
            appendArrows: '#sliderArrows',
            prevArrow: '<button type="button" class="slick-prev custom-arrow custom-prev"><img src="/icon_3_pfeil_zurueck.png" alt="Zurück" /></button>',
            nextArrow: '<button type="button" class="slick-next custom-arrow custom-next"><img src="/icon_2_pfeil_vor.png" alt="Vor" /></button>',
            pauseOnHover: true,
            adaptiveHeight: false
          });
        } catch (error) {
          console.error('Slick slider initialization failed:', error);
        }
      }
    };

    // Initialisiere direkt, da DOM durch React bereits bereit ist
    initSlider();

    // Cleanup
    return () => {
      if (window.$ && sliderRef.current && window.$.fn.slick) {
        try {
          if (window.$(sliderRef.current).hasClass('slick-initialized')) {
            window.$(sliderRef.current).slick('unslick');
          }
        } catch (error) {
          console.error('Slick slider cleanup failed:', error);
        }
      }
    };
  }, [sliderImages]); // Abhängigkeit von sliderImages, damit re-init bei Änderungen

  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      id="hero"
      sx={{
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: theme.palette.primary.white,
        color: 'main',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
    

      <Container 
        maxWidth={false} 
        className="fade-in-up"
        sx={{ 
          maxWidth: '1400px',
          px: 2, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          height: 'auto',
          mx: 'auto'
        }}
      >
        {/* Fixe Überschrift */}
        <Box sx={{ textAlign: 'center', mb: 6, px: 2, 
          padding: { xs: '80px 0 60px', md: '160px 0 120px' } }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.2rem' },
              mb: 2,
              maxWidth: '600px',
              lineHeight: 1.5,
              textAlign: 'center',
              mx: 'auto',
            }}
          >
            WILLKOMMEN IN DER
          </Typography>

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 500,
              mb: 4,
              textAlign: 'center',
            }}
          >
            CORPS GUESTPHALIA<br />
            BERLIN-DALHEIM
          </Typography>

          {/* ANIMATIONS TEST */}
          {/* <AnimatedElement>
            <div style={{ 
              background: 'lightblue', 
              padding: '20px', 
              margin: '20px 0',
              textAlign: 'center',
              fontSize: '18px'
            }}>
              DIESER TEXT SOLLTE VON UNTEN ANIMIEREN!
            </div>
          </AnimatedElement> */}

        </Box>
      </Container>

      {/* Container für den Slider - 1400px + 80px padding-left */}
      <AnimatedElement>
      <Container 
        maxWidth={false} 
        sx={{ 
          maxWidth: '1400px',
          pr: 2,
          mx: 'auto',
          mb: 8
        }}
      >
        {/* Bild-Slider */}
        <Box id="slideContainer" sx={{ position: 'relative', display: 'block' }}>
          <Box 
            id="sliderArrows" 
            sx={{
              display: 'flex',
                flexDirection: 'row',
                position: 'absolute',
                gap: 0,
                left: { xs: 0, sm: 0, md: -40, xl: -40 },
                top: { xs: -19, sm: -19, md: 80, xl: 80 },
                zIndex: 3,
              '& .custom-arrow': {
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.3s ease',
                '&:hover': {
                  opacity: 0.7
                },
                '& img': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }
              }
            }}
          ></Box>
          <Box
            ref={sliderRef}
            sx={{
              width: '100%',
              maxWidth: '800px',
              maxHeight: { xs: '300px', sm: '400px', md: 'auto' },
              position: 'relative',
              mx: 'auto',
              '& .slick-slide': {
                outline: 'none',
                padding: '0',
              },
              '& .slick-dots': {
                bottom: '-50px',
                '& li button:before': {
                  color: theme.palette.text.primary,
                  fontSize: '12px',
                },
                '& li.slick-active button:before': {
                  color: theme.palette.primary.main,
                },
              },
              '& .slick-list': {
                overflow: 'hidden',
                borderRadius: '0px',
              }
            }}
          >
            {sliderImages.map((image) => (
              <Box key={image.id}>
                <Box
                  component="img"
                  src={image.src}
                  alt={image.alt}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '0px',
                    boxShadow: 'none',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
      </AnimatedElement>

      {/* Scroll-Indikator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
        }}
        onClick={scrollToAbout}
      >

      </Box>
    </Box>
  );
};

export default HeroSection;